import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { goalActions } from '../../../../../../../../../store';
import RatesMCRow from '../common/components/ratesMCRow';

import type {
  ICommonRateMasteryCriteriaItem,
  ITreatmentPlanGoal,
} from '../../../../../../../../../utils/types';
import type { IRatesMastryCriteriaState } from '../common/types/states';

interface IPercentageMasteryCriteria {
  goal: ITreatmentPlanGoal;
  goalIndex: number;
  isEditable: boolean;
}

function isFlatRateMasteryCriteria(
  criteria: ITreatmentPlanGoal['mastery_criteria'],
): criteria is ICommonRateMasteryCriteriaItem {
  return Boolean(
    criteria &&
      'change_type' in criteria &&
      !('measurement_mode' in criteria) &&
      !('practice_duration_unit' in criteria) &&
      !('acquisition' in criteria),
  );
}

function SingleRowRatesMasteryCriteria({
  goal,
  goalIndex,
  isEditable,
}: IPercentageMasteryCriteria) {
  const dispatch = useDispatch();

  const { mastery_criteria, measurement_units } = goal;
  const rateMasteryCriteria = isFlatRateMasteryCriteria(mastery_criteria) ? mastery_criteria : null;
  const { desired_amount, practice_duration_count, change_type, total_attempts } =
    rateMasteryCriteria ?? {};

  const [masteryCriteriaState, setMasteryCriteriaMCState] = useState<IRatesMastryCriteriaState>({
    change_type: change_type ?? null,
    desired_amount: desired_amount ?? null,
    practice_duration_count: practice_duration_count ?? null,
    total_attempts: total_attempts ?? null,
  });
  const [isDirty, setIsDirty] = useState(false);

  const handleMCState = (key: string, value: number | string | null) => {
    setMasteryCriteriaMCState((prev) => ({
      ...prev,
      [key]: value,
    }));
    setIsDirty(true);
  };

  useEffect(() => {
    const { mastery_criteria } = goal;
    const rateMasteryCriteria = isFlatRateMasteryCriteria(mastery_criteria)
      ? mastery_criteria
      : null;
    const { desired_amount, practice_duration_count, change_type, total_attempts } =
      rateMasteryCriteria ?? {};
    if (!goal?.is_editing) {
      setIsDirty(false);
    }
    setMasteryCriteriaMCState({
      change_type: change_type ?? null,
      desired_amount: desired_amount ?? null,
      practice_duration_count: practice_duration_count ?? null,
      total_attempts: total_attempts ?? null,
    });
  }, [goal?.is_editing, goal?.id]);

  useEffect(() => {
    if (!isDirty) return;
    dispatch(
      goalActions.updateGoalProperty({
        goalIndex,
        goalType: goal.goal_type,
        key: 'mastery_criteria',
        value: masteryCriteriaState,
      }),
    );
    dispatch(
      goalActions.updateGoalProperty({
        goalIndex,
        goalType: goal.goal_type,
        key: 'dirty',
        value: true,
      }),
    );
  }, [masteryCriteriaState]);

  return (
    <>
      <RatesMCRow
        onUpdate={handleMCState}
        goalIndex={goalIndex}
        isEditing={Boolean(goal?.is_editing)}
        baseline_at_creation={
          goal.baseline_at_creation === null ? null : Number(goal.baseline_at_creation)
        }
        values={masteryCriteriaState}
        isEditable={isEditable}
        measurementUnits={
          measurement_units && 'interval_unit' in measurement_units ? measurement_units : null
        }
      />
    </>
  );
}

export default SingleRowRatesMasteryCriteria;
